import ListCard from '@Components/ListCard/ListCard';
import EditEmailModal from '@Components/Modals/EditEmail/EditEmailModal';
import WelcomeEmailModal from '@Components/Modals/WelcomeEmail/WelcomeEmailModal';
import EmailServices from '@Services/Email.services.ts';
import { Email } from '@Types/Asset.types';
import { PaginatedResponse } from '@Types/Response.types';
import { PAGE_PROP, PAGE_SIZE, PAGINATED_DEFAULT, PATHS, SEARCH_PROP, TABLE_STRUCTURES } from '@Utils/constants';
import { handleRequestError } from '@Utils/request.utils';
import { Button, Pagination, SearchBox, SectionCard, useTopHat } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const Emails = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [emails, setEmails] = useState<PaginatedResponse<Email>>(PAGINATED_DEFAULT);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [showWelcomeEmailModal, setShowWelcomeEmailModal] = useState<boolean>(false);
  const [selectedEmail, setSelectedEmail] = useState<Email | undefined>(undefined);
  const [showEditEmailModal, setShowEditEmailModal] = useState<boolean>(false);
  const currentPage = Number(searchParams.get(PAGE_PROP) ?? '1');
  const searchQuery = searchParams.get(SEARCH_PROP) ?? '';

  useTopHat({
    activeNav: 'nav-settings',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [
      { title: 'Settings', href: PATHS.SETTINGS, id: 'Settings' },
      {
        title: 'Email',
        href: `${PATHS.EMAIL}?page=${1}`,
        id: 'Email',
      },
    ],
  });

  const handleNewPage = (newPage: number, data: PaginatedResponse<Email>) => {
    if (newPage <= 0 || newPage > emails.totalPages) {
      return;
    }
    const page = newPage > data.totalPages ? data.prevPage : newPage;
    if (page !== currentPage) {
      searchParams.set(PAGE_PROP, newPage.toString());
      setSearchParams(searchParams);
    }
  };

  const getEmails = (page?: number) => {
    const actualPage = page || currentPage;
    setSelectedEmail(undefined);
    setIsLoading(true);
    EmailServices.searchEmail({ search: searchQuery }, actualPage)
      .then(({ data }) => {
        setEmails(data);
        setErrorMessage(undefined);
        handleNewPage(actualPage, data);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getEmails();
  }, [searchQuery, currentPage]);

  const handleSearchQueryChange = (newValue: string) => {
    searchParams.set(SEARCH_PROP, newValue);
    searchParams.set(PAGE_PROP, '1');
    if (!newValue) {
      searchParams.delete(SEARCH_PROP);
    }
    setSearchParams(searchParams);
  };

  const handleEditEmail = (id: string) => {
    setSelectedEmail(emails.docs.find((email) => email.id === id));
    setShowEditEmailModal(true);
  };

  return (
    <div className='grid'>
      <div className='col-12 col-md-3'>
        <SectionCard>
          <SearchBox
            id='search-emails'
            fullWidth
            focusOnRender
            value={searchQuery}
            defaultValue={searchQuery}
            onChange={handleSearchQueryChange}
          />
          <Button className='button-with-icon' type='primary' fullWidth onClick={() => setShowWelcomeEmailModal(true)}>
            <i className='fa-regular fa-envelope' /> Send Welcome Email
          </Button>
        </SectionCard>
      </div>
      <div className='col-12 col-md-9'>
        <ListCard<Email>
          title={'Email'}
          structure={TABLE_STRUCTURES.EMAILS}
          data={emails.docs}
          loading={isLoading}
          error={errorMessage}
          withActions={{
            edit: (id: string) => handleEditEmail(id),
          }}
          searchEntered={!!searchQuery}
        />
      </div>
      <div className='col-12 content-middle content-right-md'>
        <Pagination
          count={emails.totalDocs}
          perPage={PAGE_SIZE}
          currentPage={currentPage - 1}
          onChange={(page) => handleNewPage(page + 1, emails)}
        />
      </div>
      <WelcomeEmailModal show={showWelcomeEmailModal} onClose={() => setShowWelcomeEmailModal(false)} />
      <EditEmailModal
        show={showEditEmailModal}
        onClose={() => {
          setShowEditEmailModal(false);
          setSelectedEmail(undefined);
        }}
        onSuccess={() => getEmails(1)}
        email={selectedEmail}
      />
    </div>
  );
};

export default Emails;
